import { useState } from 'react'
import { CloseIcon } from '../lib/CloseIcon'
import { AVProject, ProfessionalDetail } from './types'
import { CUTOFF_POINT_LABELS, reverseDate } from './util'

type Props = {
  onClose: () => void
  onDelete: () => void
  onNewProject: () => void
  onResetPassword: () => void
  onSave: (professional: ProfessionalDetail) => void
  onSelectProject: (project: AVProject) => void
  professional: ProfessionalDetail
  setProfessional: (professional: ProfessionalDetail) => void
}

export function EditModal({
  onClose,
  onDelete,
  onNewProject,
  onResetPassword,
  onSave,
  onSelectProject,
  professional,
  setProfessional,
}: Props) {
  const [editingDetails, setEditingDetails] = useState(false)

  return (
    <>
      <div className="cg-modal-background"></div>
      <div className="cg-modal-dialog">
        <div className="cg-modal-title">
          <div className="flex flex-justify-between flex-align-center">
            <div>Edit professional</div>
            <div className="cg-close" onClick={onClose}>
              <CloseIcon width={22} height={22} />
            </div>
          </div>
        </div>
        <div className="cg-modal-content">
          <div className="copra-modal-section-title">Professional details</div>
          <div className="copra-info-grid">
            <div className="copra-info-row">
              <span className="copra-info-label">Bullhorn ID</span>
              <span className="copra-info-value">{professional.CodeAV || '-'}</span>
            </div>
            <div className="copra-info-row">
              <span className="copra-info-label">Email address</span>
              <span className="copra-info-value">
                {professional.Email || '-'}
              </span>
            </div>
            <div className="copra-info-row">
              <span className="copra-info-label">First name</span>
              <span className="copra-info-value">
                {professional.FirstName || '-'}
              </span>
            </div>
            <div className="copra-info-row">
              <span className="copra-info-label">Last name</span>
              <span className="copra-info-value">
                {professional.LastName || '-'}
              </span>
            </div>
          </div>

          <div className="cg-modal-actions copra-modal-actions-left">
            {professional.Email && professional.UserID && (
              <button className="cg-modal-button secondary" onClick={onResetPassword}>
                Reset password
              </button>
            )}
            <button
              className="cg-modal-button secondary"
              onClick={() => setEditingDetails(true)}
            >
              Edit details
            </button>
            <button className="cg-modal-button secondary" onClick={onDelete}>
              Delete
            </button>
          </div>

          <div className="copra-project-header">
            <div className="copra-modal-section-title">Projects</div>
            <button className="cg-modal-button" onClick={onNewProject}>
              New project
            </button>
          </div>

          <div className="copra-project-table">
            <div className="copra-project-table-header">
              <div>Code</div>
              <div>Start date</div>
              <div>End date</div>
              <div>Entity</div>
              <div>Cutoff</div>
              <div>Unit</div>
              <div>Reimbursed</div>
              <div>Deductible</div>
              <div>Approver</div>
            </div>
            {professional.projects.length === 0 && (
              <div className="copra-project-empty">No projects linked yet.</div>
            )}
            {professional.projects.map((project) => (
              <button
                key={project.ID}
                className="copra-project-row"
                onClick={() => onSelectProject(project)}
                type="button"
              >
                <div>{project.ProjectCode}</div>
                <div>{reverseDate(project.StartDate)}</div>
                <div>{reverseDate(project.EndDate)}</div>
                <div>{project.MagnoEntity}</div>
                <div>
                  {project.CutoffPoint
                    ? CUTOFF_POINT_LABELS[project.CutoffPoint]
                    : ''}
                </div>
                <div>{project.UnitType || ''}</div>
                <div>
                  {project.ExpensesReimbursed ? 'Exp ' : ''}
                  {project.TravelReimbursed ? 'KM' : ''}
                </div>
                <div>
                  {project.TravelDeductible ? 'KM ' : ''}
                  {project.TravelExpensesDeductible ? 'TrvExp ' : ''}
                  {project.ExpensesDeductible ? 'OthExp ' : ''}
                  {project.WorkedFromHome ? 'WFH' : ''}
                </div>
                <div>
                  {(project.Approvers || [])
                    .map((approver) => approver.Name)
                    .join(', ')}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {editingDetails && (
        <>
          <div className="cg-modal-background nested"></div>
          <div className="cg-modal-new-dialog cg-modal-details-dialog">
            <div className="cg-modal-title">
              <div className="flex flex-justify-between flex-align-center">
                <div>Edit details</div>
                <div className="cg-close" onClick={() => setEditingDetails(false)}>
                  <CloseIcon width={22} height={22} />
                </div>
              </div>
            </div>
            <div className="cg-modal-content cg-modal-content-new-professional">
              <div className="copra-modal-grid copra-modal-grid-new-professional">
                <label className="copra-field copra-field-full">
                  <span>Bullhorn ID</span>
                  <input
                    value={professional.CodeAV}
                    onChange={(e) =>
                      setProfessional({
                        ...professional,
                        CodeAV: e.target.value,
                      })
                    }
                  />
                </label>
                <label className="copra-field">
                  <span>First name</span>
                  <input
                    value={professional.FirstName}
                    onChange={(e) =>
                      setProfessional({
                        ...professional,
                        FirstName: e.target.value,
                      })
                    }
                  />
                </label>
                <label className="copra-field">
                  <span>Last name</span>
                  <input
                    value={professional.LastName}
                    onChange={(e) =>
                      setProfessional({
                        ...professional,
                        LastName: e.target.value,
                      })
                    }
                  />
                </label>
                <label className="copra-field copra-field-full">
                  <span>Email address</span>
                  <input
                    value={professional.Email || ''}
                    onChange={(e) =>
                      setProfessional({
                        ...professional,
                        Email: e.target.value,
                      })
                    }
                  />
                </label>
              </div>
              <div className="cg-modal-actions">
                <button
                  className="cg-modal-button secondary"
                  onClick={() => setEditingDetails(false)}
                >
                  Cancel
                </button>
                <button
                  className="cg-modal-button"
                  onClick={async () => {
                    await onSave(professional)
                    setEditingDetails(false)
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
